import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { ILoginAPIResponse } from '../../models/auth.model';
import { IRecentlyAccessedItem, ISearchSummaryModel } from '../../models/search.model';

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    private currentLoginUser: ILoginAPIResponse | null = null;
    private readonly DEVICE_ID_KEY = 'interseedDeviceId';
    private readonly MAX_RECENT_ITEMS = 10;
    private readonly RECENT_SEARCHES_KEY = 'interseedRecentSearches';
    private readonly RECENTLY_ACCESSED_KEY = 'interseedRecentlyAccessed';
    private readonly USER_KEY = 'interseedUser';

    constructor(@Inject(PLATFORM_ID) private platformId: object) {}

    addRecentlyAccessed(item: IRecentlyAccessedItem): void {
        if (!this.isBrowser()) {
            return;
        }

        const items = this.getRecentlyAccessed();

        const filtered = items.filter((existing) => existing.data.id !== item.data.id);

        filtered.unshift(item);
        const trimmed = filtered.slice(0, this.MAX_RECENT_ITEMS);

        localStorage.setItem(this.RECENTLY_ACCESSED_KEY, JSON.stringify(trimmed));
    }

    addRecentSearch(searchItem: ISearchSummaryModel): void {
        if (!this.isBrowser()) {
            return;
        }

        const items = this.getRecentSearches();

        // Remove if already exists
        const filtered = items.filter((item) => item.name !== searchItem.name);

        // Add to beginning
        filtered.unshift(searchItem);
        const trimmed = filtered.slice(0, this.MAX_RECENT_ITEMS);

        localStorage.setItem(this.RECENT_SEARCHES_KEY, JSON.stringify(trimmed));
    }

    getDeviceId(): string | null {
        if (!this.isBrowser()) {
            return null;
        }
        let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
        }
        return deviceId;
    }

    getLoginUser(): ILoginAPIResponse | null {
        if (!this.isBrowser()) {
            return null;
        }
        if (this.currentLoginUser !== null) {
            return this.currentLoginUser;
        }

        const encryptUser = localStorage.getItem(this.USER_KEY);
        if (!encryptUser) {
            return null;
        }

        try {
            const binaryString = atob(encryptUser);
            const utf8Bytes = new Uint8Array([...binaryString].map((char) => char.charCodeAt(0)));
            const stringBaseUser = new TextDecoder().decode(utf8Bytes);
            this.currentLoginUser = JSON.parse(stringBaseUser);
        } catch (e) {
            console.error('LoginStorage', e);
        }

        return this.currentLoginUser;
    }

    getRecentlyAccessed(): IRecentlyAccessedItem[] {
        if (!this.isBrowser()) {
            return [];
        }
        const stored = localStorage.getItem(this.RECENTLY_ACCESSED_KEY);
        return stored ? JSON.parse(stored) : [];
    }

    getRecentSearches(): ISearchSummaryModel[] {
        if (!this.isBrowser()) {
            return [];
        }
        const stored = localStorage.getItem(this.RECENT_SEARCHES_KEY);
        return stored ? JSON.parse(stored) : [];
    }

    setLoginUser(loginProfile: ILoginAPIResponse): void {
        if (!this.isBrowser()) {
            return;
        }
        this.currentLoginUser = loginProfile;
        const stringBaseUser = JSON.stringify(loginProfile);
        const utf8Bytes = new TextEncoder().encode(stringBaseUser);
        const binaryString = String.fromCodePoint(...utf8Bytes);
        const encryptUser = btoa(binaryString);
        localStorage.setItem(this.USER_KEY, encryptUser);
    }

    clearLoginUser(): void {
        if (!this.isBrowser()) {
            return;
        }
        this.currentLoginUser = null;
        localStorage.removeItem(this.USER_KEY);
        localStorage.removeItem(this.RECENTLY_ACCESSED_KEY);
        localStorage.removeItem(this.RECENT_SEARCHES_KEY);
    }

    getAccessToken(): string | null {
        if (this.currentLoginUser === null) {
            return null;
        }

        return this.currentLoginUser.accessToken;
    }

    getRefreshToken(): string | null {
        if (this.currentLoginUser === null) {
            return null;
        }

        return this.currentLoginUser.refreshToken;
    }

    removeRecentlyAccessed(itemToRemove: IRecentlyAccessedItem): void {
        if (!this.isBrowser()) {
            return;
        }

        const items = this.getRecentlyAccessed();
        const filtered = items.filter((item) => item.data.id !== itemToRemove.data.id);

        localStorage.setItem(this.RECENTLY_ACCESSED_KEY, JSON.stringify(filtered));
    }

    removeRecentSearch(searchItemToRemove: ISearchSummaryModel): void {
        if (!this.isBrowser()) {
            return;
        }

        const items = this.getRecentSearches();
        const filtered = items.filter((item) => item.name !== searchItemToRemove.name);

        localStorage.setItem(this.RECENT_SEARCHES_KEY, JSON.stringify(filtered));
    }

    updateToken(accessToken: string, refreshToken?: string): void {
        if (!this.isBrowser()) {
            return;
        }

        const user = this.getLoginUser();
        if (!user) {
            return;
        }

        user.accessToken = accessToken;
        if (refreshToken) {
            user.refreshToken = refreshToken;
        }

        this.setLoginUser(user);
    }

    updateNameEmail(name: string, email: string): void {
        if (!this.isBrowser()) {
            return;
        }

        const user = this.getLoginUser();
        if (!user) {
            return;
        }

        user.name = name;
        user.email = email;

        this.setLoginUser(user);
    }

    private isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }
}
